import React, { useState } from "react";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import clsx from "clsx";
import { Button } from "antd";
import { PlusCircleOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router";
import dayjs from "dayjs";
import Table from "../../../Common/Table";
import { DatePicker } from "antd";
import NotificationViewModal from "./View";
import { useTranslation } from "react-i18next";
import {
  NotificationStatus,
  NotificationTableRow,
  useNotificationTableGet,
} from "../../../../hooks/api/notifications";
import { RequestData } from "@ant-design/pro-components";
import { SortOrder } from "antd/es/table/interface";
import { queryFilterParams, getSorterParams } from "../../../../utils";
import { dateTimeFormat } from "../../../../contstant";
import NotificationModal from "./NotificationModal";

const { RangePicker } = DatePicker;

export const badgeColors: Record<NotificationStatus, string> = {
  [NotificationStatus.sent]: "green",
  [NotificationStatus.draft]: "blue",
};

export const NotificationStatusLabels: Record<NotificationStatus, string> = {
  [NotificationStatus.sent]: "Sent",
  [NotificationStatus.draft]: "Draft",
};

export enum UserGroup {
  All = "all",
  Admin = "admin",
  Guest = "guest",
}

/**
 * How many people are in each group.
 *
 * TODO(design): needs the real group sizes from the API — a notification has
 * no recipient count on it, and neither has the group. The numbers below are
 * placeholders so the column and the form can be reviewed. The campaigns
 * section has the same need, with its own audiences (see AUDIENCES there).
 */
export const USER_GROUP_SIZE: Record<UserGroup, number> = {
  [UserGroup.All]: 12480,
  [UserGroup.Admin]: 24,
  [UserGroup.Guest]: 3120,
};

export const USER_GROUP_ENUM: Record<UserGroup, { text: string }> =
  Object.values(UserGroup).reduce(
    (acc, group) => ({
      ...acc,
      [group]: { text: group.charAt(0).toUpperCase() + group.slice(1) },
    }),
    {} as Record<UserGroup, { text: string }>,
  );

function ToolBar({ onCreate }: { onCreate: () => void }) {
  const { t } = useTranslation();

  return [
    <Button
      key="add"
      icon={<PlusCircleOutlined />}
      type="primary"
      onClick={(e) => {
        e.preventDefault();
        onCreate();
      }}
    >
      {t("common.create")}
    </Button>,
  ];
}

export default function Outbox(): React.ReactNode | null {
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  const [createOpen, setCreateOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any | null>(null);
  const { t, i18n } = useTranslation();
  const notificationTableGet = useNotificationTableGet();

  const columns: ProColumns[] = [
    {
      title: t("pages.notificationCenter.title"),
      dataIndex: "title",
      key: "title",
      hideInSearch: true,
    },
    {
      title: t("pages.notificationCenter.message"),
      dataIndex: "description",
      key: "description",
      hideInSearch: true,
      ellipsis: true,
    },
    {
      title: t("pages.notificationCenter.userGroup"),
      dataIndex: "group",
      key: "group",
      sorter: true,
      valueType: "select",
      valueEnum: USER_GROUP_ENUM,
      onFilter: (value, record) => record.userGroup === value,
    },
    {
      title: t("pages.notificationCenter.sent"),
      key: "sent",
      hideInSearch: true,
      // No dataIndex and no sorter: the number is read from the group rather
      // than from the row, so there is nothing on the record to sort by and
      // nothing for the request to order on.
      render: (_, record) =>
        USER_GROUP_SIZE[record.group as UserGroup]?.toLocaleString() ?? "—",
    },
    {
      title: t("pages.notificationCenter.sentAt"),
      dataIndex: "createdAt",
      key: "createdAt",
      hideInSearch: true,
      sorter: true,
      renderText: (text) => dayjs(text).format(dateTimeFormat),
    },
    {
      title: t("pages.notificationCenter.sentAt"),
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      valueType: "dateRange",
      hideInTable: true,
      search: {
        transform: (value?: [string, string]) => {
          if (!value || value.length !== 2) return {};

          return {
            createdFrom: value[0],
            createdTo: value[1],
          };
        },
      },
    },
  ];

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number | undefined;
      current?: number | undefined;
      keyword?: string | undefined;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<NotificationTableRow>>> => {
    const newParams = queryFilterParams({
      page: current ? `${current}` : "1",
      pageSize: pageSize ? `${pageSize}` : "10",
      ...args,
      ...getSorterParams(sorter),
    });

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });

    return notificationTableGet
      .fetch({
        ...newParams,
        name: newParams?.name || undefined,
      })
      .then((response) => {
        if (response) {
          const { data, total } = response;

          return {
            data: (data || []).map((item) => ({
              ...item,
              key: item.id,
            })),
            success: true,
            total,
          };
        }

        return { data: [], success: false, total: 0 };
      });
  };

  return (
    <>
      <Table
        key={`${tableKey}-${i18n.language}`}
        resetKey={() => setTableKey(Date.now())}
        className={clsx("transparent")}
        headerTitle={t("pages.notificationCenter.notificationsList")}
        toolBarRender={() => [
          <ToolBar key="toolbar" onCreate={() => setCreateOpen(true)} />,
        ]}
        request={tableRequest}
        columns={columns}
        actions={[
          [
            "view",
            ({ record }) => ({
              title: "View notification",
              onClick: (e: MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedRecord(record);
                setViewOpen(true);
              },
            }),
          ],
        ]}
        search={{
          style: {
            // display: 'none',
          },
        }}
        options={{
          search: {
            name: "name",
          },
        }}
        columnsState={{
          persistenceKey: "pro-table-notification",
          persistenceType: "localStorage",
        }}
      />

      <NotificationViewModal
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        data={selectedRecord}
      />
      <NotificationModal
        open={createOpen}
        mode="create"
        onClose={() => setCreateOpen(false)}
        setTableKey={() => setTableKey(Date.now())}
      />
    </>
  );
}
